import hashParams from '@gameday/app/lib/store/hashParams';
import getAdobeAppConfig from '@gameday/utils/getAdobeAppConfig';
import deviceLib from 'device';

const DEVICESTRING = 'ios';
const INVENTORYSTR = 'xgameday';
const INVENTORYSTR_WV = 'xgameday_wv';
const INVENTORYSTR_LIVE_WV = 'xgameday-live-wv';

const getSiteConfig = () => {
    let appParams;
    let locale;
    let timecode;
    const appInfoSource = document.getElementById('appInfo');
    const params = new URLSearchParams(location.search);
    const env = window?.dataEnv || 'prod';
    const { rsid, adobeMc } = getAdobeAppConfig();
    const deviceBootstrap = deviceLib();
    const deviceType = deviceBootstrap.formFactor; // this return desktop for ipad for some reason
    const device = (deviceType === 'phone' ? 'iphone' : 'ipad');
    const adDevice = `${device === 'iphone' ? 'phone' : 'tablet'}${device ? '/' : ''}${device}`;
    let readHash = true;

    if (appInfoSource) {
        readHash = false;
        appParams = (appInfoSource && JSON.parse(appInfoSource.innerText)) || {};
        timecode = params.get('tfs');
        locale = appInfoSource?.locale;
    } else {
        appParams = hashParams();
        timecode = appParams?.timecode;
        locale = appParams?.lang || 'en';
    }

    if (locale !== 'en' && locale !== 'es' && locale !== 'ja') {
        locale = 'en';
    }

    const adLocaleStr = `${locale === 'es' ? 'es.' : (locale === 'ja' ? 'ja.' : '')}`;

    const adConfig = {
        adRefreshRate: 30,
        env,
        adUnitHierarchy1: `${adLocaleStr}mlb.mlb`,
        adUnitHierarchy2: `/${INVENTORYSTR_WV}/${adDevice}`,
        inventoryHierarchy: '/' + INVENTORYSTR_WV,
        thresholds: [0, 670, 768, 980, 1024],
        thresholdsMap: ['xs', 's', 'm', 'l', 'xl'],
        thresholdsMediaQueries: [
            window.matchMedia('(max-width: 669px)'),
            window.matchMedia('(min-width: 670px) and (max-width: 767px)'),
            window.matchMedia('(min-width: 768px) and (max-width: 979px)'),
            window.matchMedia('(min-width: 980px) and (max-width: 1023px)'),
            window.matchMedia('(min-width: 1024px)')
        ],
        units: {
            presby: {
                refresh: false,
                init: true,
                container: 'ad-114x48-presby',
                sizes: [
                    [114, 48]
                ],
                thresholds: [0, 1, 2, 3, 4, 5],
                inventoryHierarchy: `/${INVENTORYSTR_WV}/${adDevice}`,
                targeting: {
                    pos: '1'
                }
            },
            parallax: {
                refresh: true,
                init: true,
                container: 'ad-300x250-parallax',
                sizes: [[300, 250]],
                thresholds: [0, 1, 2, 3, 4, 5],
                inventoryHierarchy: `/${INVENTORYSTR_WV}/${adDevice}`,
                targeting: {
                    pos: '2'
                }
            },
            midInning: [{
                refresh: false,
                init: true,
                container: 'ad-300x250-midinning',
                sizes: [[300, 250]],
                thresholds: [0, 1, 2, 3, 4, 5],
                inventoryHierarchy: `/${INVENTORYSTR_LIVE_WV}/${adDevice}`,
                targeting: {
                    pos: '1'
                }
            }]
        },
        responsive: true,
        iu: `/2605/${adLocaleStr}mlb.mlb/${INVENTORYSTR_WV}/${adDevice}`,
        pp: 'mlb_csai_vod_mobile',
        prerollIu: {
            highlight: `/2605/${adLocaleStr}mlb.video/${INVENTORYSTR_WV}/${adDevice}`,
            midinning: `/2605/${adLocaleStr}${INVENTORYSTR}.video/mlb/web-view`
        },
        video: {
            adSources: [
                'publica',
                'aps'
            ],
            publica: {
                siteIds: {
                    midinning: 29387,
                    highlight: 57815
                },
                site_name: 'mlb',
                site_page: 'https://www.mlb.com/',
                custom_6: 'major'
            }
        }
    };

    const properties = {
        enableSW: true,
        mount: '#gameday-index-component__root',
        game: {
            locale,
            game: appParams?.game_pk || appParams?.game || '',
            game_state: appParams?.game_state || '',
            game_tab: appParams?.game_tab || '',
            dataMode: 'gumbo',
            game_pk: appParams?.game_pk,
            logging: 'none',
            debug: true,
            scoreboard: {
                preferMLB: true
            },
            basename: '/',
            detail: appParams?.detail || '',
            server: false
        },
        config: {
            env,
            logging: 'log',
            adRefreshRate: 60,
            appView: DEVICESTRING,
            locale,
            siteProperty: 'mlb',
            dictionary: {},
            siteLabel: 'MLB',
            seoSitePrefix: 'mlb',
            enableFieldVision: true,
            enableFieldVisionOD: true,
            forceFieldVisionOn: false,
            forceDFV: false,
            videoAdsVolume: '10',
            videoAdsMute: true,
            showVideoAds: true,
            batEnabled: true,
            appPath: 'app/atbat/',
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            disableWebSockets: false,
            webSocketWindow: 18,
            nativeVideoPlayer: false,
            enableWebEntitlement: false,
            enablePOTG: false,
            useFallbackTeamUrl: true,
            hashState: {
                read: readHash,
                write: readHash,
                watch: false,
                separator: '&' // Need to use & for iOS as JSON payload hooks expect it
            }
        },
        sponsor: '',
        tracking: {
            suite: {
                desktop: rsid,
                mobile: rsid
            },
            props: {
                pageName: 'Major League Baseball: Gameday',
                channel: 'Gameday',
                prop1: 'Gameday Initial Load',
                currencyCode: 'USD',
                omnitureSetting: {
                    eVar73: 'mlbglobal08,mlbcom08'
                }
            }
        },
        skin: {},
        rules: {},
        adConfig
    };

    const appOverride = params.get('appOverride');
    const enableFieldVisionOverride = params.get('enableFieldVision');
    const enableFieldVisionODOverride = params.get('enableFieldVisionOD');
    const forceFieldVisionOn = params.get('forceFV') === 'true';
    const forceWebSockets = params.get('forceWS');
    const forceDFV = params.get('forceDFV') === 'true';
    const stitchEnv = params.get('stitch_env');
    const adobeTargetProperty = '64bd9a62-f522-8775-9b0d-4984abae73c4';
    const enablePOTG = params.get('enablePOTG');

    window.AppAdobeTrackingConfig = { ...properties.tracking, ...{ locale, adobeMc, adobeTargetProperty } };

    if (timecode) {
        properties.game.timecode = timecode;
    }

    if (appOverride !== null) {
        properties.config.appOverride = appOverride;
    }

    if (enableFieldVisionOverride !== null) {
        properties.config.enableFieldVision = enableFieldVisionOverride === 'true';
    }

    if (enableFieldVisionODOverride !== null) {
        properties.config.enableFieldVisionOD = enableFieldVisionODOverride === 'true';
    }

    if (stitchEnv !== null) {
        properties.config.stitchEnv = stitchEnv;
    }

    if (enablePOTG !== null) {
        properties.config.enablePOTG = true;
    }

    properties.config.forceFieldVisionOn = forceFieldVisionOn;
    properties.config.forceWebSockets = forceWebSockets === 'true' ? true : forceWebSockets === 'false' ? false : null;
    properties.config.forceDFV = forceDFV;

    return properties;
};

export default getSiteConfig();
